import { DecisionGroupCircles } from "@/components/shared";
import { DecisionGroup } from "@/components/shared/DecisionGroupCircles";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { PendingBidDecisionGroup, FetchPendingBidDecisionGroupsQuery, FetchPendingBidDecisionGroupsQueryVariables, FetchPendingBidDecisionGroupsDocument } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import useUserStore from "@/store/shield";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

function PendingBidDecisions() {

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const navigate = useNavigate();
  const { data, loading } = useQuery<FetchPendingBidDecisionGroupsQuery, FetchPendingBidDecisionGroupsQueryVariables>(FetchPendingBidDecisionGroupsDocument, {
    fetchPolicy: "cache-and-network",
  });

  const bidDecisionGroups = data?.fetchPendingBidDecisionGroups ?? [];
  const organizationId = useUserStore((state) => state.orgId);

  const onClickHandler = (bidDecisionGroup: PendingBidDecisionGroup) => {
    if (!bidDecisionGroup) return;

    navigate(`/tender/${bidDecisionGroup.tenderId}/biddecisions/${bidDecisionGroup.groups[0].id}`);
  };

  // Handle navigation when a specific group circle is clicked
  const onGroupClickHandler = (bidDecisionGroup: PendingBidDecisionGroup, group: DecisionGroup) => {
    if (!bidDecisionGroup || !group) return;

    // Find the matching group in the bidDecisionGroup.groups array
    const matchingGroup = bidDecisionGroup.groups.find(
      (g: any) => g.operationalOrganizationId === group.operationalOrganizationId
    );

    if (matchingGroup) {
      // Navigate to the specific group's bid decision page
      navigate(`/tender/${bidDecisionGroup.tenderId}/biddecisions/${matchingGroup.id}?openAccordion=item-0`);
    }
  };

  const isBidTurn = (bidDecisionGroup: Partial<PendingBidDecisionGroup>) => {
    if (!bidDecisionGroup.groups) return false;

    return bidDecisionGroup.groups.find((group: any) => group.awaitingOrganizationId === organizationId)
      ? true
      : false;
  };

  /**
   * Sort bid decision groups by whether it is the user's turn to bid.
   */
  const sortedBidDecisionGroups = [...bidDecisionGroups].sort((a, b) => {
    const isBidTurnA = isBidTurn(a as Partial<PendingBidDecisionGroup>) ? 0 : 1;
    const isBidTurnB = isBidTurn(b as Partial<PendingBidDecisionGroup>) ? 0 : 1;
    return isBidTurnA - isBidTurnB;
  });

  return (
    <Card data-cy="dashboard-bid-decision-container" className="grow flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="pl-2 text-base flex justify-between items-center">
          {t("biddecision.title")}
          <HoverCard>
            <HoverCardTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 p-0 hover:cursor-help"
              >
                <InformationCircleIcon className="h-5 w-5" />
              </Button>
            </HoverCardTrigger>
            <HoverCardContent className="w-80 text-sm font-normal">
              {t("biddecision.tooltip")}
            </HoverCardContent>
          </HoverCard>
        </CardTitle>
      </CardHeader>
      <CardContent className="h-full pr-3">
        <div className="flex flex-col gap-2 h-full" data-cy="dashboard-bid-decision-content-wrapper">
          {
            loading && Array.from({length: 4}).map((_, index) => (
              <div key={`bid-decision-loading-skeleton-${index}`} className="flex gap-4 items-center w-full px-2" aria-hidden="true">
                <div className="flex grow flex-col gap-2">
                  <Skeleton className="w-1/3 h-4" />
                  <Skeleton className="w-2/3 h-2" />
                </div>
                <Skeleton className="w-14 h-8" />
              </div>
            ))
          }

          {/* Height calculated based on height of header, card content, card title, and card header and assumed pending margin decision component */}
          {!loading && (
            <ScrollArea className="flex flex-col gap-2 pr-3 max-h-[300px] lg:max-h-none lg:h-[calc(50vh-8.5rem)]">
              {
                bidDecisionGroups.length === 0 && (
                  <div className="text-sm text-gray-500" data-cy="dashboard-bid-decision-content-empty">
                    {t("biddecision.nobiddecisions")}
                  </div>
                )
              }
              {
                sortedBidDecisionGroups.map((bidDecisionGroup) => {
                  const isMyTurn = isBidTurn(bidDecisionGroup as Partial<PendingBidDecisionGroup>);

                  return (
                    <div key={`bid-decision-group-${bidDecisionGroup?.tenderId}`}>
                      <div
                        className="hover:bg-gray-100 rounded-md py-1 px-2 cursor-pointer"
                        onClick={() => onClickHandler(bidDecisionGroup)}
                      >
                        <div className="flex gap-4 items-center text-sm" data-cy="dashboard-bid-decision-content-item">
                          <div className="flex grow flex-col min-w-0">
                            <div className="line-clamp-2">{bidDecisionGroup.name}</div>
                            <div className={cn("text-xs", isMyTurn ? "text-orange-500" : "text-gray-500")}>
                              {isMyTurn ? t("biddecision.awaitingresponse") : t("biddecision.awaitingotherresponse")}
                            </div>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <DecisionGroupCircles
                              groups={bidDecisionGroup.groups as DecisionGroup[]}
                              onGroupClick={(group) => onGroupClickHandler(bidDecisionGroup, group)}
                            />
                          </div>
                        </div>
                      </div>
                      <Separator className="my-1" />
                    </div>
                  );
                })
              }
            </ScrollArea>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default PendingBidDecisions;
